
import { useState, useRef } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import AuthModal from '../../../components/AuthModal';
import { emergencyContacts } from '../../../mocks/emergency';

const priorityContacts = emergencyContacts.filter((c) => c.priority);
const otherContacts = emergencyContacts.filter((c) => !c.priority);

export default function PrintableEmergencyCard() {
  const { isAuthenticated, loading } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const handleDownload = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    triggerPrint();
  };

  const triggerPrint = () => {
    if (!printRef.current) return;
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank', 'width=800,height=1100');
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Roseville Emergency Contacts Card</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Inter', sans-serif; background: #fff; color: #1a1a1a; }
          .card-page { width: 7.5in; margin: 0 auto; padding: 0.4in; }
          .card-header { background: linear-gradient(135deg, #0d9488, #0f766e); color: white; padding: 20px 24px; border-radius: 12px 12px 0 0; display: flex; align-items: center; justify-content: space-between; }
          .card-header h1 { font-size: 22px; font-weight: 800; letter-spacing: -0.5px; }
          .card-header p { font-size: 11px; opacity: 0.85; margin-top: 2px; }
          .card-header .badge { background: rgba(255,255,255,0.2); padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 600; }
          .card-body { border: 2px solid #e5e7eb; border-top: none; border-radius: 0 0 12px 12px; padding: 20px 24px 16px; }
          .section-title { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #0d9488; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 2px solid #ccfbf1; display: flex; align-items: center; gap: 6px; }
          .section-title .dot { width: 8px; height: 8px; background: #ef4444; border-radius: 50%; }
          .contact-row { display: flex; justify-content: space-between; align-items: center; padding: 7px 0; border-bottom: 1px dashed #e5e7eb; }
          .contact-row:last-child { border-bottom: none; }
          .contact-name { font-size: 12px; font-weight: 500; color: #374151; flex: 1; }
          .contact-phone { font-size: 13px; font-weight: 700; color: #1a1a1a; text-align: right; min-width: 140px; }
          .contact-phone.critical { color: #dc2626; font-size: 15px; }
          .contact-hours { font-size: 10px; color: #9ca3af; text-align: right; min-width: 100px; }
          .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 16px; }
          .grid-section { }
          .footer-bar { margin-top: 16px; padding: 12px 16px; background: #f0fdfa; border-radius: 8px; display: flex; justify-content: space-between; align-items: center; }
          .footer-bar p { font-size: 10px; color: #6b7280; }
          .footer-bar .url { font-weight: 700; color: #0d9488; }
          .cut-line { border-top: 2px dashed #d1d5db; margin: 24px 0; position: relative; }
          .cut-line::before { content: '✂'; position: absolute; top: -10px; left: -4px; font-size: 14px; color: #9ca3af; }
          .mini-card { border: 2px solid #0d9488; border-radius: 12px; padding: 16px 20px; }
          .mini-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 2px solid #ccfbf1; }
          .mini-header h2 { font-size: 16px; font-weight: 800; color: #0d9488; }
          .mini-header span { font-size: 10px; color: #6b7280; }
          .mini-row { display: flex; justify-content: space-between; padding: 4px 0; }
          .mini-row .name { font-size: 11px; color: #374151; }
          .mini-row .phone { font-size: 12px; font-weight: 700; color: #1a1a1a; }
          .mini-row.critical .phone { color: #dc2626; }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .card-page { padding: 0.3in; }
          }
        </style>
      </head>
      <body>
        ${printContent}
        <script>window.onload = function() { window.print(); }<\/script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <>
      <section className="py-20 bg-gradient-to-br from-teal-50 via-white to-amber-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 text-teal-600 bg-teal-50 text-sm font-medium px-4 py-1.5 rounded-full mb-4">
                <i className="ri-printer-line"></i>
                Printable Resource
              </span>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Emergency Contacts Card</h2>
              <p className="text-gray-600 text-sm max-w-xl mx-auto">
                Download and print a wallet-sized emergency contacts card. Keep it on your fridge, in your car, or in your wallet for quick access.
              </p>
            </div>

            {/* Preview Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-8">
              {/* Card Visual Preview */}
              <div className="p-8 bg-gradient-to-br from-gray-50 to-white">
                <div className="max-w-2xl mx-auto">
                  {/* Mini preview of the card */}
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex items-center justify-between">
                      <div>
                        <h3 className="text-white font-extrabold text-lg tracking-tight">ROSEVILLE<span className="text-amber-300">.org</span></h3>
                        <p className="text-white/80 text-xs mt-0.5">Emergency Contacts Card</p>
                      </div>
                      <span className="bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full">2025</span>
                    </div>
                    <div className="px-6 py-4">
                      {/* Critical Numbers Preview */}
                      <p className="text-xs font-bold text-teal-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <span className="w-2 h-2 bg-red-500 rounded-full inline-block"></span>
                        Critical Numbers
                      </p>
                      <div className="space-y-1.5 mb-4">
                        {priorityContacts.slice(0, 4).map((c) => (
                          <div key={c.id} className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">{c.name}</span>
                            <span className={`text-sm font-bold ${c.phone === '911' ? 'text-red-600' : 'text-gray-900'}`}>{c.phone}</span>
                          </div>
                        ))}
                      </div>
                      {/* Blurred preview of remaining content */}
                      <div className="relative">
                        <div className="space-y-1.5 filter blur-[3px] select-none">
                          {otherContacts.slice(0, 4).map((c) => (
                            <div key={c.id} className="flex justify-between items-center">
                              <span className="text-xs text-gray-600">{c.name}</span>
                              <span className="text-sm font-bold text-gray-900">{c.phone}</span>
                            </div>
                          ))}
                        </div>
                        {!isAuthenticated && !loading && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 shadow-sm border border-gray-200">
                              <p className="text-xs text-gray-500 flex items-center gap-1.5">
                                <i className="ri-lock-line text-teal-600"></i>
                                Sign in to download full card
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Bar */}
              <div className="px-8 py-6 bg-white border-t border-gray-100">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center">
                      <i className="ri-file-pdf-2-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Roseville Emergency Contacts Card</p>
                      <p className="text-xs text-gray-500">Full-page + wallet-size format • Printable</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {isAuthenticated && (
                      <button
                        onClick={() => setShowPreview(!showPreview)}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className={`${showPreview ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                        {showPreview ? 'Hide Preview' : 'Preview'}
                      </button>
                    )}
                    <button
                      onClick={handleDownload}
                      disabled={loading}
                      className="flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 transition-colors shadow-lg shadow-teal-600/20 whitespace-nowrap cursor-pointer disabled:opacity-50"
                    >
                      {!isAuthenticated ? (
                        <>
                          <i className="ri-lock-line"></i>
                          Sign In to Download
                        </>
                      ) : (
                        <>
                          <i className="ri-download-2-line"></i>
                          Download &amp; Print
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {!isAuthenticated && !loading && (
                  <div className="mt-4 flex items-center gap-2 text-xs text-gray-500 bg-amber-50 border border-amber-100 rounded-lg px-4 py-2.5">
                    <i className="ri-information-line text-amber-500 text-base"></i>
                    <span>Create a free Roseville.org account to download the printable emergency contacts card. It only takes a moment.</span>
                  </div>
                )}
              </div>
            </div>

            {/* Full Preview (only for authenticated users) */}
            {isAuthenticated && showPreview && (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mb-8 animate-fadeIn">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-gray-900">Full Preview</h3>
                  <button
                    onClick={triggerPrint}
                    className="flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-printer-line"></i>
                    Print Now
                  </button>
                </div>
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 bg-gray-50">
                  {/* Full page card preview */}
                  <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden max-w-2xl mx-auto">
                    <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex items-center justify-between">
                      <div>
                        <h3 className="text-white font-extrabold text-lg">ROSEVILLE<span className="text-amber-300">.org</span></h3>
                        <p className="text-white/80 text-xs">Emergency Contacts Card — Roseville, CA</p>
                      </div>
                      <span className="bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full">2025</span>
                    </div>
                    <div className="px-6 py-5">
                      <p className="text-xs font-bold text-teal-600 uppercase tracking-wider mb-3 flex items-center gap-1.5 pb-2 border-b-2 border-teal-100">
                        <span className="w-2 h-2 bg-red-500 rounded-full inline-block"></span>
                        Critical Emergency Numbers
                      </p>
                      <div className="space-y-2 mb-5">
                        {priorityContacts.map((c) => (
                          <div key={c.id} className="flex justify-between items-center py-1 border-b border-dashed border-gray-100 last:border-0">
                            <span className="text-xs font-medium text-gray-700">{c.name}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-[10px] text-gray-400">{c.hours}</span>
                              <span className={`text-sm font-bold ${c.phone === '911' ? 'text-red-600 text-base' : 'text-gray-900'}`}>{c.phone}</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-xs font-bold text-teal-600 uppercase tracking-wider mb-3 pb-2 border-b-2 border-teal-100">Medical &amp; Law Enforcement</p>
                          <div className="space-y-2">
                            {otherContacts.filter(c => ['medical', 'law'].includes(c.category)).map((c) => (
                              <div key={c.id} className="flex justify-between items-center py-0.5">
                                <span className="text-[11px] text-gray-600 pr-2">{c.name}</span>
                                <span className="text-xs font-bold text-gray-900 whitespace-nowrap">{c.phone}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-teal-600 uppercase tracking-wider mb-3 pb-2 border-b-2 border-teal-100">Utilities &amp; Community</p>
                          <div className="space-y-2">
                            {otherContacts.filter(c => ['utility', 'community', 'crisis', 'fire'].includes(c.category)).map((c) => (
                              <div key={c.id} className="flex justify-between items-center py-0.5">
                                <span className="text-[11px] text-gray-600 pr-2">{c.name}</span>
                                <span className="text-xs font-bold text-gray-900 whitespace-nowrap">{c.phone}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="mt-5 pt-3 border-t border-gray-100 flex justify-between items-center">
                        <p className="text-[10px] text-gray-400">Keep this card in a visible location • Updated 2025</p>
                        <p className="text-[10px] font-bold text-teal-600">roseville.org/emergency</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Features */}
            <div className="grid sm:grid-cols-3 gap-5">
              <div className="bg-white rounded-xl border border-gray-100 p-5 text-center shadow-sm">
                <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <i className="ri-fridge-line text-teal-600 text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-gray-900 mb-1"><a href="#" className="hover:text-teal-600 transition-colors">Fridge-Ready</a></h4>
                <p className="text-xs text-gray-500">Full-page format perfect for posting on your refrigerator or bulletin board</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-5 text-center shadow-sm">
                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <i className="ri-wallet-3-line text-amber-600 text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-gray-900 mb-1"><a href="#" className="hover:text-teal-600 transition-colors">Wallet-Size</a></h4>
                <p className="text-xs text-gray-500">Includes a cut-out wallet card with the most critical numbers</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-5 text-center shadow-sm">
                <div className="w-10 h-10 bg-rose-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <i className="ri-refresh-line text-rose-600 text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-gray-900 mb-1"><a href="#" className="hover:text-teal-600 transition-colors">Always Updated</a></h4>
                <p className="text-xs text-gray-500">Numbers verified and updated regularly so you always have accurate info</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hidden printable content */}
      <div className="hidden">
        <div ref={printRef}>
          <div className="card-page">
            {/* Full Page Card */}
            <div className="card-header">
              <div>
                <h1>ROSEVILLE<span style={{ color: '#fbbf24' }}>.org</span></h1>
                <p>Emergency Contacts Card — Roseville, California</p>
              </div>
              <div className="badge">2025</div>
            </div>
            <div className="card-body">
              <div className="section-title">
                <span className="dot"></span>
                Critical Emergency Numbers
              </div>
              {priorityContacts.map((c) => (
                <div className="contact-row" key={c.id}>
                  <span className="contact-name">{c.name}</span>
                  <span className="contact-hours">{c.hours}</span>
                  <span className={`contact-phone ${c.phone === '911' ? 'critical' : ''}`}>{c.phone}</span>
                </div>
              ))}

              <div className="grid-2">
                <div className="grid-section">
                  <div className="section-title" style={{ marginTop: '8px' }}>Medical &amp; Law Enforcement</div>
                  {otherContacts.filter(c => ['medical', 'law'].includes(c.category)).map((c) => (
                    <div className="contact-row" key={c.id}>
                      <span className="contact-name">{c.name}</span>
                      <span className="contact-phone">{c.phone}</span>
                    </div>
                  ))}
                </div>
                <div className="grid-section">
                  <div className="section-title" style={{ marginTop: '8px' }}>Utilities, Crisis &amp; Community</div>
                  {otherContacts.filter(c => ['utility', 'community', 'crisis', 'fire'].includes(c.category)).map((c) => (
                    <div className="contact-row" key={c.id}>
                      <span className="contact-name">{c.name}</span>
                      <span className="contact-phone">{c.phone}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="footer-bar">
                <p>Keep this card in a visible location • Numbers verified 2025</p>
                <p className="url">roseville.org/emergency</p>
              </div>
            </div>

            {/* Cut Line */}
            <div className="cut-line"></div>

            {/* Wallet-Size Mini Card */}
            <div className="mini-card">
              <div className="mini-header">
                <h2>ROSEVILLE<span style={{ color: '#0d9488' }}>.org</span> — Emergency</h2>
                <span>Wallet Card • 2025</span>
              </div>
              {priorityContacts.map((c) => (
                <div className={`mini-row ${c.phone === '911' ? 'critical' : ''}`} key={c.id}>
                  <span className="name">{c.name}</span>
                  <span className="phone">{c.phone}</span>
                </div>
              ))}
              <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '6px', paddingTop: '6px' }}>
                {otherContacts.slice(0, 6).map((c) => (
                  <div className="mini-row" key={c.id}>
                    <span className="name">{c.name}</span>
                    <span className="phone">{c.phone}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          setTimeout(() => triggerPrint(), 500);
        }}
      />

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </>
  );
}
